/* eslint-disable camelcase */
import React, {
  FC, useContext, useEffect, useMemo,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import memoize from 'lodash/memoize';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  BoothFetchedLevel, BoothFetchingState, fetchBoothById, selectBoothById, selectBoothNotFoundById,
  selectBoothRootContentElementById, selectFetchingStateByBoothId,
} from 'models/cms/booths';
import { selectXtraEvent } from 'models/cms/events';
import { selectLocale } from 'models/localization';
import { fetchBoothProducts } from 'models/boothProducts';
import { selectNonEmptyBoothTabsByIds } from 'models/cms/boothTabs';
import useCachedCMSDataTreatment from 'hooks/useCachedCMSDataTreatment';
import { SplitioContext } from 'components/SplitioContext';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import CustomHelmet from 'components/CustomHelmet';
import ScrollableContainerWrapper from 'components/ScrollableContainerWrapper';

import { selectLocalizedEventName } from 'models/virtual_event';
import { LightboxContextStateProvider } from 'components/booth/CustomLightbox/LightboxContext';
import { TabsContextStateProvider } from 'components/TabsContext';
import Routes from 'Routes';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import { liveStreamActions, LiveStreamBoothChannel, LiveStreamType } from 'models/liveStream';
import { LocalizableString } from 'models/cms/common/types';
import BoothExhibitorPanel from 'components/exhibitorPanel/BoothExhibitorPanel';
import { defer, of } from 'rxjs';
import { getCMSRequests } from 'network/cmsRequests';
import { catchError, map } from 'rxjs/operators';
import { useObservable } from 'react-use';
import { UserSessionContext } from 'components/UserSession';
import BoothProfileContainer from './BoothProfileContainer';

const RootContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background-color: #FFF;
`;

const Container = styled.div<{ wrapperBgColor?: string, isExhibitorPanel?: boolean }>`
  width: auto;
  display: block;
  overflow: ${({ isExhibitorPanel }) => (isExhibitorPanel ? 'hidden' : 'auto')};

  ${({ isExhibitorPanel }) => (isExhibitorPanel ? 'height: 100%;' : '')}
  .booth-template-wrapper {
    width: 100%;
    background: ${({ wrapperBgColor }) => wrapperBgColor || 'initial'};
  }
`;

const EmptyState = styled.div`
  padding: 50px;
  text-align: center;
`;

const NotFoundContent = styled.div`
  padding: 10vh 0;
`;

const BoothNotFoundContainer = () => {
  const { t } = useTranslation();
  const { features, pageNames } = useSelector((state: any) => state.eventConfigs);
  const eventName = useSelector(selectLocalizedEventName);
  const locale = useSelector(selectLocale);
  const hallName = useMemo(() => pageNames?.hall?.[locale] || t('page.hall', 'Exhibition Hall'), [locale, pageNames?.hall, t]);

  return (
    <EmptyState>
      <CustomHelmet>
        <meta charSet="utf-8" />
        <title>{`Booth Not Found | ${eventName}`}</title>
        <meta name="description" content={`Booth not found in ${eventName} event.`} />
      </CustomHelmet>
      <NotFoundContent>
        <h3>The booth is temporarily unavailable.</h3>
        <h3>Please contact the organizer.</h3>
        {!features.includes('hideHall') && (
          <Link to="/hall">{`Back to the ${hallName}`}</Link>
        )}
      </NotFoundContent>
    </EmptyState>
  );
};

const selectFetching = memoize((id: string) => (state: any) => {
  const fetchingState = selectFetchingStateByBoothId(state, id);
  return !fetchingState || fetchingState === BoothFetchingState.Pending;
});

const BoothPageTabsWrapper: FC<{ boothId: string }> = ({ boothId, children }) => {
  const { tabs } = useSelector((state) => selectBoothRootContentElementById(state, boothId), shallowEqual);
  const boothTabs = useSelector(selectNonEmptyBoothTabsByIds(tabs, boothId));

  return (
    <TabsContextStateProvider tabs={boothTabs}>
      {children}
    </TabsContextStateProvider>
  );
};

const BoothPageContentContainer = ({ isPreview }: { isPreview?: boolean }) => {
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const { boothId } = useParams<any>();
  const { id: eventId } = useSelector(selectXtraEvent) || {};

  const fetching = useSelector(selectFetching(boothId), shallowEqual);
  const isBoothNotFound = useSelector((s) => selectBoothNotFoundById(s, boothId), shallowEqual);

  const features = useSelector((s: any) => s.eventConfigs.features);

  const cachedCMSDataReady = useSelector((state: any) => state.cms.events.cachedCMSDataReady);
  const { isReady: isSplitioReady } = useContext(SplitioContext);
  const cachedCMSData = useCachedCMSDataTreatment();

  useEffect(() => {
    if (!boothId || !eventId || !isSplitioReady || (cachedCMSData && !cachedCMSDataReady)) return;
    dispatch(fetchBoothById({ boothId, expectedLevel: BoothFetchedLevel.Details }));
  }, [
    boothId,
    dispatch,
    fetching,
    eventId,
    locale,
    isSplitioReady,
    cachedCMSData,
    cachedCMSDataReady,
  ]);

  useEffect(() => {
    if (!boothId || !eventId) return;
    if (features.includes('productPage')) {
      dispatch(fetchBoothProducts(boothId));
    }
  }, [boothId, eventId, dispatch, features]);

  const enabledLiveStreamPeriods = useSelector((state) => state.eventConfigs as any)?.liveStream?.boothIdLiveStreamPeriodsMap?.[boothId] ?? [];
  const haveLiveStreamPeriods = enabledLiveStreamPeriods?.length > 0;
  const booth = useSelector((state) => selectBoothById(state, boothId), shallowEqual);

  const userSession = useContext(UserSessionContext);
  const isBoothReps$ = useMemo(() => defer(() => getCMSRequests().fetchCanPerformOnBooth({ id: boothId })).pipe(
    map(({ data }) => (data as (typeof data & { __typename: 'Booth' })).canPerformVisitUnrestricted),
    catchError(((err) => {
      console.error(err);
      return of(false);
    })),
  ), [boothId]);

  const isBoothRep = useObservable(isBoothReps$);
  const loading = fetching || isBoothRep === undefined;

  useEffect(() => {
    if (loading) return undefined;
    if (isBoothRep) dispatch(liveStreamActions.joinChannel(null));
    else if (haveLiveStreamPeriods && !isPreview) {
      dispatch(liveStreamActions.joinChannel({
        channel: {
          type: LiveStreamType.Booth,
          data: {
            boothId,
            boothName: booth?.name as LocalizableString,
          },
        } as LiveStreamBoothChannel,
      }));
      dispatch(liveStreamActions.updateIsClosable(false));

      return () => dispatch(liveStreamActions.updateIsClosable(true));
    }
    return undefined;
  }, [booth?.name, boothId, dispatch, haveLiveStreamPeriods, isBoothRep, isPreview, loading]);

  const isExhibitorPanel = useMemo(() => isBoothRep && !isPreview && haveLiveStreamPeriods, [haveLiveStreamPeriods, isBoothRep, isPreview]);

  return (
    <RootContainer id="booth-profile-root">
      <ScrollableContainerWrapper
        WrapperContainer={Container}
        id="booth-container"
        wrapperBgColor="initial"
        isExhibitorPanel={isExhibitorPanel}
      >
        <BoothPageTabsWrapper boothId={boothId}>
          <>
            {loading && <CircularLoadingIndicator />}
            {!loading && (
              !isBoothNotFound
                ? (
                  <LightboxContextStateProvider>
                    {
                      (isExhibitorPanel)
                        ? <BoothExhibitorPanel boothId={boothId} />
                        : <BoothProfileContainer boothId={boothId} isPreview={isPreview} />
                    }
                  </LightboxContextStateProvider>
                )
                : <BoothNotFoundContainer />
            )}
          </>
        </BoothPageTabsWrapper>
        <FloatingFooter hashState={Routes.booth(boothId)} />
      </ScrollableContainerWrapper>
    </RootContainer>
  );
};

BoothPageContentContainer.defaultProps = {
  isPreview: false,
};

export default BoothPageContentContainer;
